import React, { useContext, useMemo } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import groupBy from 'lodash/groupBy'
import { CallListManagerContext, CallOnList } from '~/service/CallListManager'
import Timer from '../common/Timer'
import { CampaignManagerContext } from '~/service/CampaignManager'
import { FormattedMessage } from '~/translations/utils'
import trans from '~/translations/trans'
import MainContent from '../MainContent'
import { FilterButton, FilterRadio } from '../common/List'
import { formatDate, formatRelative } from '~/utils/dateFns'
import Search from '~/components/common/Search'
import useResizeObserver from 'use-resize-observer'
import TableHeader from './list/TableHeader'
import humanizeDuration from 'humanize-duration'
import Player from './list/Player'

const List: React.FC = ({}) => {
  const {
    calls,
    filter,
    setFilter,
    loadHook,
    loadMore,
    loading,
    search,
    setSearch,
  } = useContext(CallListManagerContext)

  const { ref, width = 0 } = useResizeObserver<HTMLElement>()

  loadHook()

  const groups = useMemo(
    () => groupBy(calls, call => formatDate(new Date(call.createdAt), 'P')),
    [calls.map(call => call.id + '-' + call.ver).join()]
  )

  const filterButtonCommon = { filter, setFilter }

  return (
    <>
      <div className="main-header list-header" ref={ref}>
        <Search
          filter={filter}
          setFilter={setFilter}
          search={search}
          setSearch={setSearch}
          small={width > 1200}
        />

        <div className="list-filter">
          <FilterRadio
            {...filterButtonCommon}
            buttons={[
              {
                name: 'all',
                label: intl.formatMessage({ id: 'calls / filter / all' }),
              },
              {
                name: 'answered',
                label: intl.formatMessage({ id: 'calls / filter / answered' }),
              },
              {
                name: 'unanswered',
                label: intl.formatMessage({
                  id: 'calls / filter / unanswered',
                }),
              },
            ]}
          />

          <FilterRadio
            {...filterButtonCommon}
            buttons={[
              {
                name: 'all',
                label: intl.formatMessage({ id: 'calls / filter / all' }),
              },
              {
                name: 'inbound',
                label: intl.formatMessage({ id: 'calls / filter / inbound' }),
              },
              {
                name: 'outbound',
                label: intl.formatMessage({
                  id: 'calls / filter / outbound',
                }),
              },
            ]}
          />

          <FilterButton
            {...filterButtonCommon}
            name="mine"
            label={intl.formatMessage({ id: 'calls / filter / mine' })}
          />

          {/* <FilterButton
            {...filterButtonCommon}
            name="toDescribe"
            label={intl.formatMessage({ id: 'calls / filter / to describe' })}
          /> */}
        </div>
      </div>
      <div className="main-columns-item">
        <div className="list-table-wrapper-header">
          <TableHeader width={width} />
        </div>
        <MainContent onScrolledToEnd={() => loadMore()}>
          <div className="list call-list">
            {Object.values(groups).length
              ? Object.values(groups).map((calls, k) => (
                  <Items calls={calls} key={k} width={width} />
                ))
              : !loading && (
                  <div className="list-empty">
                    <FormattedMessage id="calls / empty" />
                  </div>
                )}

            <div
              className={classNames('list-loading', {
                'is-active': loading,
              })}
            >
              <span className="fa fa-spin fa-spinner-third" />
            </div>
          </div>
        </MainContent>
      </div>
    </>
  )
}

type ListOnlyProps = {
  items: CallOnList[]
  width?: number
}

export const ListOnly: React.FC<ListOnlyProps> = ({ items, width = 0 }) => {
  const groups = useMemo(
    () => groupBy(items, call => formatDate(new Date(call.createdAt), 'P')),
    [items.map(call => call.id + '-' + call.ver).join()]
  )

  return (
    <div className="list">
      {Object.values(groups).length ? (
        Object.values(groups).map((calls, k) => (
          <Items calls={calls} key={k} width={width} />
        ))
      ) : (
        <div className="list-empty">Brak połączeń</div>
      )}
    </div>
  )
}

type ItemsProps = {
  calls: CallOnList[]
  width: number
}

const Items: React.FC<ItemsProps> = ({ calls, width }) => {
  return (
    <>
      <div className="list-items-header call-list-items-header">
        <div className="list-items-header-date">
          {formatRelative(new Date(calls[0].createdAt))}
        </div>
        <div className="list-items-header-count">{calls.length}</div>
      </div>
      <div className="list-items call-list-items">
        {calls.map(call => (
          <Item call={call} key={call.id} width={width} />
        ))}
      </div>
    </>
  )
}

type ItemProps = {
  call: CallOnList
  width: number
}

const Item: React.FC<ItemProps> = ({ call, width }) => {
  const campaignContext = useContext(CampaignManagerContext)
  const maxWidth = 1000

  return (
    <div
      className={classNames('list-item call-list-item', {
        'is-missed': call.missed,
        'is-answered': !call.missed,
        'is-outbound': call.outbound,
        'is-inbound': !call.outbound,
      })}
    >
      <Link to={'/calls/' + call.id} className="call-list-item-avatar">
        <img src={call.client.avatar} />
      </Link>
      <Link to={'/calls/' + call.id} className="call-list-item-status">
        <span className="fa fa-arrow-up" />
      </Link>
      <Link
        to={'/calls/' + call.id}
        className={classNames('call-list-item-client', {
          'call-list-item-client--wide': width <= maxWidth,
        })}
      >
        {call.client.name}
      </Link>

      {width > maxWidth && (
        <>
          <Link to={'/calls/' + call.id} className="call-list-item-number">
            {call.number}
          </Link>
          <Link to={'/calls/' + call.id} className="call-list-item-campaign">
            {call.tmCampaignId && (
              <>
                <span className="far fa-bullhorn" />
                {campaignContext.campaignsMap[call.tmCampaignId].name}
              </>
            )}
          </Link>
        </>
      )}

      <Link to={'/calls/' + call.id} className="call-list-item-user">
        {call.user ? (
          <span>
            <img
              src={call.user.avatar}
              title={call.user.name.replace(/^(.)[^\s]*\s/, '$1. ')}
            />
          </span>
        ) : call.voicebot ? (
          <span className="fa fa-robot" />
        ) : (
          '-'
        )}
      </Link>
      {width > maxWidth && (
        <>
          <Link to={'/calls/' + call.id} className="call-list-item-duration">
            {!call.missed && call.ended && !call.voicebot
              ? humanizeDuration(call.duration, {
                  round: true,
                  language: intl.locale,
                })
              : '-'}
          </Link>

          <div className="call-list-item-recording">
            {call.recorded ? <Player item={call} /> : '-'}
          </div>

          <Link to={'/calls/' + call.id} className="call-list-item-ivr">
            {call.ivr}
          </Link>
        </>
      )}

      <Link to={'/calls/' + call.id} className="call-list-item-date">
        {!call.answered && !call.ended ? (
          <span className="fa fa-spin fa-spinner-third" />
        ) : call.answeredAt && !call.ended ? (
          <>
            <Timer initial={new Date(call.answeredAt)} />
            <span className="fa fa-spin fa-spinner-third" />
          </>
        ) : (
          formatDate(new Date(call.createdAt), 'p')
        )}
      </Link>
    </div>
  )
}

export default List
